import * as React from 'react'
// libs
import { useRouter } from 'hooks/useRouter'
import { useSelector } from 'react-redux'
import { useQuery, useMutation } from '@apollo/client/react'
// context
import { TableManagementContext } from 'containers/Search/layers/BL'
// store
import { objectEntitiesSelector } from 'store/reducers/objectEntities'
// constants
import { OBJECT_ENTITY_NAMES } from 'constants/objects'
// graphql
import { PLANS_LIST_QUERY } from 'graphql/plan/queries'
import { FILTER_SIDEBAR_QUERY } from 'graphql/filter/queries'
import { PLAN_DELETE_MUTATION } from 'graphql/plan/mutations'
// types
import { TRefetch } from 'types/refetch'
import { NGetPlan } from 'types/plan/helpers'
import { ConfigItem } from 'layouts/types'
import { FilterTarget, IField, IPlan } from 'types/graphql'
// utils
import get from 'lodash/get'
import find from 'lodash/fp/find'
import pipe from 'lodash/fp/pipe'
import getFp from 'lodash/fp/get'
import { safeGetOr } from 'utils/fp'
import { handleError } from 'utils/errors/handlers'
import { getFieldsList } from 'utils/fields'
import { getSidebarConfig } from 'utils/layouts'

type Props = React.PropsWithChildren<unknown>

interface IContext {
  data: IPlan[]
  config: ConfigItem[]
  fields: IField[]
  refetch: TRefetch<NGetPlan.IResponse, NGetPlan.IVariables>
  isLoading: boolean
  deletePlan: (id: string) => Promise<void>
  pageFilter: string
  totalCount: number
  isRefetching: boolean
}

export const PlansPageContext = React.createContext<IContext>({} as any)

const PlansPageAPI: React.FC<Props> = ({ children }) => {
  const router = useRouter()

  const querySlug = router.query.slug?.[0]

  const { search, filter, page, perPage } = React.useContext(TableManagementContext)

  const objectEntities = useSelector(objectEntitiesSelector)

  const { data: filtersQuery, loading } = useQuery(FILTER_SIDEBAR_QUERY, {
    variables: {
      filter: {
        target: FilterTarget.Plan
      }
    }
  })

  const { data, networkStatus, refetch } = useQuery(PLANS_LIST_QUERY, {
    fetchPolicy: 'network-only',
    variables: {
      filter: {
        ...filter,
        name: search,
        filterSlug: querySlug
      },
      pagination: {
        pageSkip: page * perPage,
        pageTake: perPage
      }
    }
  })

  const [deleteMutation] = useMutation(PLAN_DELETE_MUTATION)

  const filters = safeGetOr<any, any, any>([], 'filters.data')(filtersQuery)
  const config = getSidebarConfig(filters, 'plans')

  const pageFilter = React.useMemo(() => {
    if (querySlug) {
      return pipe(
        find(({ slug }) => querySlug === slug),
        getFp('name')
      )(filters)
    }
  }, [filters, querySlug])

  const plans: IPlan[] = get(data, 'plans.data', [])
  const fields = getFieldsList(objectEntities, OBJECT_ENTITY_NAMES.PLAN)
  const totalCount: number = get(data, 'plans.pagination.totalCount')

  const deletePlan = async (id: string) => {
    try {
      await deleteMutation({
        variables: { id }
      })

      await refetch()
    } catch (errors) {
      handleError(errors, 'PlansPageAPI.deletePlan')
    }
  }

  React.useEffect(() => {
    if (filters.length && !pageFilter) {
      router.push(`/plans/${filters[0].slug}`)
    }
  }, [filters, pageFilter])

  const value = {
    config,
    fields,
    refetch,
    deletePlan,
    totalCount,
    pageFilter,
    data: plans,
    isLoading: loading || networkStatus === 1,
    isRefetching: networkStatus === 2
  } as IContext

  return <PlansPageContext.Provider value={value}>{children}</PlansPageContext.Provider>
}

export default PlansPageAPI
